import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Share, 
  RefreshCw, 
  Brain, 
  Target, 
  Users, 
  Zap, 
  DollarSign, 
  Code,
  Sparkles,
  CheckCircle,
  Download,
  Heart,
  Star,
  TrendingUp,
  Lightbulb,
  Mic,
  ArrowRight
} from 'lucide-react';
import { AppConcept, GenerationStage } from '../types';
import { generateAppConcept } from '../services/aiService';
import { useAuth } from '../contexts/AuthContext';
import { saveConcept } from '../services/firebaseService';

interface ConceptGeneratorProps {
  userIdea: string;
  onConceptGenerated: (concept: AppConcept) => void;
  onBack: () => void;
}

const ConceptGenerator: React.FC<ConceptGeneratorProps> = ({ userIdea, onConceptGenerated, onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStage, setCurrentStage] = useState(0);
  const [generatedConcept, setGeneratedConcept] = useState<AppConcept | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [inputIdea, setInputIdea] = useState('');
  const [showInputForm, setShowInputForm] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const hasStartedGeneration = useRef(false);
  
  // Get user idea from route state, prop, or input
  const routeIdea = (location.state as any)?.userIdea;
  const hasInitialIdea = routeIdea || userIdea; // Only route or prop ideas trigger auto-generation
  const hasIdea = hasInitialIdea || inputIdea;
  const ideaToGenerate = hasIdea || "A random innovative idea";

  const stages: GenerationStage[] = [
    { stage: 'Analyzing', description: 'Analyzing your shower thought', completed: false },
    { stage: 'Problem', description: 'Identifying the core problem', completed: false },
    { stage: 'Features', description: 'Designing core features', completed: false },
    { stage: 'Business', description: 'Planning business model', completed: false },
    { stage: 'Tech', description: 'Selecting tech stack', completed: false },
  ];

  const generateConcept = useCallback(async () => {
    if (isGenerating || hasStartedGeneration.current) return;
    
    hasStartedGeneration.current = true;
    setIsGenerating(true);
    setError(null);
    setGeneratedConcept(null);
    setShowInputForm(false);
    
    try {
      console.log('🚀 Starting concept generation for:', ideaToGenerate);
      
      // Simulate stage progression
      for (let i = 0; i < stages.length; i++) {
        setCurrentStage(i);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      console.log('🤖 Calling Gemini API...');
      const concept = await generateAppConcept(ideaToGenerate);
      console.log('✅ Concept generated:', concept);
      
      setGeneratedConcept(concept);
      setIsGenerating(false);
      
      // Automatically save concept when generated
      console.log('💾 Auto-saving generated concept to parent...');
      onConceptGenerated(concept);
      
      // Navigate to gallery and auto-show the concept after a short delay
      setTimeout(() => {
        navigate('/gallery', { state: { showConceptId: concept.id } });
      }, 2000);
      
    } catch (err) {
      console.error('❌ Error generating concept:', err);
      setError('Failed to generate concept. Please try again.');
      setIsGenerating(false);
      hasStartedGeneration.current = false; // Reset on error so user can retry
    }
  }, [ideaToGenerate, onConceptGenerated, isGenerating, navigate, stages.length]);

  useEffect(() => {
    console.log('🔄 ConceptGenerator state check:', {
      hasIdea: hasIdea,
      hasIdeaValue: `"${hasIdea}"`,
      generatedConcept: !!generatedConcept,
      isGenerating,
      showInputForm,
      routeIdea,
      userIdea,
      inputIdea,
      hasStartedGeneration: hasStartedGeneration.current
    });
    
    // Only start generating if we have an INITIAL idea (from route/prop) and haven't started generation yet
    if (hasInitialIdea && !hasStartedGeneration.current && !generatedConcept && !isGenerating) {
      console.log('🚀 Starting generation because we have initial idea and haven\'t started yet');
      generateConcept();
    } else if (!hasInitialIdea && !generatedConcept && !isGenerating && !showInputForm) {
      // Show input form if no initial idea provided and no concept generated
      console.log('📝 Showing input form - no initial idea and no concept');
      setShowInputForm(true);
    } else {
      console.log('❌ Not generating because:', {
        hasInitialIdea: !!hasInitialIdea,
        hasStartedGeneration: hasStartedGeneration.current,
        generatedConcept: !!generatedConcept,
        isGenerating: isGenerating,
        condition1: hasInitialIdea && !hasStartedGeneration.current && !generatedConcept && !isGenerating,
        condition2: !hasInitialIdea && !generatedConcept && !isGenerating && !showInputForm
      });
    }
  }, [hasInitialIdea, generatedConcept, isGenerating, showInputForm, generateConcept, hasIdea, routeIdea, userIdea, inputIdea]);

  const handleSave = async () => {
    if (generatedConcept && currentUser) {
      try {
        console.log("🔄 Manual save button clicked for concept:", generatedConcept.id);
        
        // Save to Firebase directly (concept was already auto-saved, but ensure it's there)
        await saveConcept(generatedConcept, currentUser.uid);
        
        // Provide immediate user feedback
        setIsSaved(true);
        setTimeout(() => setIsSaved(false), 2000);
        
        console.log("✅ Concept manually saved to Firebase successfully");
      } catch (error) {
        console.error("❌ Error saving concept:", error);
        alert("Failed to save concept. Please try again.");
      }
    } else if (!currentUser) {
      alert("Please sign in to save concepts.");
    } else {
      console.warn("⚠️ No concept to save");
    }
  };
  const handleShare = async () => {
    if (generatedConcept && navigator.share) {
      try {
        await navigator.share({
          title: `${generatedConcept.appName} - App Concept`,
          text: `Check out this app concept I generated: ${generatedConcept.appName} - ${generatedConcept.tagline}`,
          url: window.location.href,
        });
      } catch (err) {
        // Fallback to clipboard
        navigator.clipboard.writeText(`${generatedConcept.appName} - ${generatedConcept.tagline}\n\n${generatedConcept.problem}`);
        alert('Concept copied to clipboard!');
      }
    } else if (generatedConcept) {
      // Fallback for browsers without native share
      navigator.clipboard.writeText(`${generatedConcept.appName} - ${generatedConcept.tagline}\n\n${generatedConcept.problem}`);
      alert('Concept copied to clipboard!');
    }
  };

  const handleVoiceInput = () => {
    if ('webkitSpeechRecognition' in window) {
      const recognition = new (window as any).webkitSpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setInputIdea(transcript);
        setIsListening(false);
      };

      recognition.onerror = () => {
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.start();
    } else {
      alert('Voice recognition not supported in this browser');
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputIdea.trim()) {
      setShowInputForm(false);
      hasStartedGeneration.current = false; // Reset so generation can start
      generateConcept();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (inputIdea.trim()) {
        handleFormSubmit(e as any);
      }
    }
  };

  const placeholders = [
    "What if I could text my plant?",
    "What if my pet could order their own food?",
    "What if my water bottle reminded me to drink?",
    "What if my calendar could reschedule itself?",
    "What if my headphones chose music based on my heartbeat?",
    "What if my toothbrush rated my brushing?"
  ];

  if (error) {
    return (
      <div className="concept-generator error-state">
        <button onClick={() => navigate('/gallery')} className="back-button">
          <ArrowLeft size={20} />
          Back to Gallery
        </button>
        <div className="error-message">
          <h2>😅 Oops!</h2>
          <p>{error}</p>
          <button onClick={generateConcept} className="retry-button">
            <RefreshCw size={16} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (showInputForm) {
    return (
      <div className="modern-generator">
        {/* Navigation */}
        <nav className="generator-nav">
          <div className="nav-container">
            <button onClick={() => navigate('/gallery')} className="nav-back-btn">
              <ArrowLeft size={20} />
              <span>Back to Gallery</span>
            </button>
            <div className="nav-logo">
              <Brain size={24} />
              <span>New Concept</span>
            </div>
          </div>
        </nav>

        {/* Input Form */}
        <div className="generator-container">
          <div className="input-form-section">
            <div className="input-header">
              <div className="input-icon">
                <Lightbulb size={48} />
              </div>
              <h1 className="input-title">Share Your Shower Thought</h1>
              <p className="input-subtitle">
                Describe any random idea, even if it sounds silly. Our AI will transform it into a complete app concept.
              </p>
            </div>

            <form onSubmit={handleFormSubmit} className="idea-input-form">
              <div className="input-group">
                <label htmlFor="idea-input">What's your idea?</label>
                <textarea
                  id="idea-input"
                  value={inputIdea}
                  onChange={(e) => setInputIdea(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholders[Math.floor(Math.random() * placeholders.length)]}
                  className="idea-textarea"
                  rows={6}
                  maxLength={500}
                />
                <div className="input-footer">
                  <span className="character-count">{inputIdea.length}/500</span>
                  <button
                    type="button"
                    onClick={handleVoiceInput}
                    className={`voice-btn ${isListening ? 'listening' : ''}`}
                    disabled={isListening}
                  >
                    <Mic size={16} />
                    {isListening ? 'Listening...' : 'Voice'}
                  </button>
                </div>
              </div>
              
              <button
                type="submit"
                className="generate-btn"
                disabled={!inputIdea.trim() || inputIdea.length > 500}
              >
                <Sparkles size={20} />
                Generate App Concept
                <ArrowRight size={16} />
              </button>
            </form>

            <div className="input-examples">
              <h3>Need inspiration? Try these:</h3>
              <div className="example-pills">
                <button 
                  type="button"
                  onClick={() => setInputIdea("What if I could text my plant?")} 
                  className="example-pill"
                >
                  Plant texting
                </button>
                <button 
                  type="button"
                  onClick={() => setInputIdea("What if my pet could order their own food?")} 
                  className="example-pill"
                >
                  Pet food ordering
                </button>
                <button 
                  type="button"
                  onClick={() => setInputIdea("What if my water bottle reminded me to drink?")} 
                  className="example-pill"
                >
                  Smart hydration
                </button>
                <button 
                  type="button"
                  onClick={() => setInputIdea("What if my headphones chose music based on my heartbeat?")} 
                  className="example-pill"
                >
                  Heartbeat music
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isGenerating) {
    return (
      <div className="modern-generator">
        {/* Navigation */}
        <nav className="generator-nav">
          <div className="nav-container">
            <button onClick={() => navigate('/gallery')} className="nav-back-btn">
              <ArrowLeft size={20} />
              <span>Back to Gallery</span>
            </button>
            <div className="nav-logo">
              <Brain size={24} />
              <span>Generating Concept</span>
            </div>
          </div>
        </nav>

        {/* Loading Content */}
        <div className="generator-container">
          <div className="loading-section">
            <div className="loading-header">
              <div className="loading-icon">
                <Sparkles size={48} className="sparkles-icon" />
              </div>
              <h1 className="loading-title">Creating Your App Concept</h1>
              <p className="loading-subtitle">From: "{ideaToGenerate}"</p>
            </div>

            <div className="progress-container">
              <div className="progress-steps">
                {stages.map((stage, index) => (
                  <div 
                    key={stage.stage}
                    className={`progress-step ${index <= currentStage ? 'active' : ''} ${index < currentStage ? 'completed' : ''}`}
                  >
                    <div className="step-indicator">
                      {index < currentStage ? (
                        <CheckCircle size={20} />
                      ) : index === currentStage ? (
                        <div className="step-loading">
                          <div className="step-spinner"></div>
                        </div>
                      ) : (
                        <div className="step-number">{index + 1}</div>
                      )}
                    </div>
                    <div className="step-content">
                      <div className="step-title">{stage.stage}</div>
                      <div className="step-description">{stage.description}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${((currentStage + 1) / stages.length) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="loading-stats">
              <div className="stat-item">
                <Brain size={16} />
                <span>AI Processing</span>
              </div>
              <div className="stat-item">
                <Lightbulb size={16} />
                <span>Analyzing Ideas</span>
              </div>
              <div className="stat-item">
                <TrendingUp size={16} />
                <span>Building Future</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!generatedConcept) return null;

  return (
    <div className="modern-generator">
      {/* Navigation */}
      <nav className="generator-nav">
        <div className="nav-container">
          <button onClick={() => navigate('/gallery')} className="nav-back-btn">
            <ArrowLeft size={20} />
            <span>Back to Gallery</span>
          </button>
          <div className="nav-actions">
            <button 
              onClick={() => setIsLiked(!isLiked)} 
              className={`nav-action-btn ${isLiked ? 'liked' : ''}`}
            >
              <Heart size={16} fill={isLiked ? 'currentColor' : 'none'} />
            </button>
            <button onClick={handleSave} className={`nav-action-btn ${isSaved ? 'saved' : ''}`}>
              {isSaved ? <CheckCircle size={16} /> : <Save size={16} />}
              <span>{isSaved ? 'Saved!' : 'Save'}</span>
            </button>
            <button onClick={handleShare} className="nav-action-btn">
              <Share size={16} />
              <span>Share</span>
            </button>
            <button onClick={() => {
              setGeneratedConcept(null);
              setInputIdea('');
              setShowInputForm(true);
              hasStartedGeneration.current = false;
            }} className="nav-action-btn primary">
              <RefreshCw size={16} />
              <span>Generate New</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="generator-container">
        {/* Header Section */}
        <div className="concept-header-section">
          <div className="concept-hero">
            <div className="app-badge">
              <div className="app-emoji">{generatedConcept.emoji}</div>
              <div className="success-indicator">
                <CheckCircle size={16} />
                <span>Concept Generated</span>
              </div>
            </div>
            <h1 className="app-name">{generatedConcept.appName}</h1>
            <p className="app-tagline">"{generatedConcept.tagline}"</p>
            <div className="concept-meta">
              <div className="meta-item">
                <Target size={14} />
                <span>Problem-Solving App</span>
              </div>
              <div className="meta-item">
                <Users size={14} />
                <span>{Array.isArray(generatedConcept.targetAudience) ? generatedConcept.targetAudience.length : 1} Target Groups</span>
              </div>
              <div className="meta-item">
                <Zap size={14} />
                <span>{generatedConcept.coreFeatures.length} Core Features</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="concept-content-grid">
          {/* Problem Section */}
          <div className="content-card featured">
            <div className="card-header">
              <div className="card-icon">
                <Target size={24} />
              </div>
              <div className="card-title">
                <h2>The Problem</h2>
                <p>What we're solving</p>
              </div>
            </div>
            <div className="card-content">
              <p className="problem-text">{generatedConcept.problem}</p>
            </div>
          </div>

          {/* Target Audience */}
          <div className="content-card">
            <div className="card-header">
              <div className="card-icon">
                <Users size={24} />
              </div>
              <div className="card-title">
                <h2>Target Audience</h2>
                <p>Who will use this</p>
              </div>
            </div>
            <div className="card-content">
              <div className="audience-list">
                {(Array.isArray(generatedConcept.targetAudience) ? generatedConcept.targetAudience : [generatedConcept.targetAudience]).map((audience, index) => (
                  <div key={index} className="audience-item">
                    <div className="audience-icon">👥</div>
                    <span>{audience}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* MVP Scope */}
          <div className="content-card">
            <div className="card-header">
              <div className="card-icon">
                <Star size={24} />
              </div>
              <div className="card-title">
                <h2>MVP Scope</h2>
                <p>Start with this</p>
              </div>
            </div>
            <div className="card-content">
              <p>{generatedConcept.mvpScope}</p>
            </div>
          </div>

          {/* Monetization */}
          <div className="content-card">
            <div className="card-header">
              <div className="card-icon">
                <DollarSign size={24} />
              </div>
              <div className="card-title">
                <h2>Monetization</h2>
                <p>How to make money</p>
              </div>
            </div>
            <div className="card-content">
              <p>{generatedConcept.monetization}</p>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="features-section">
          <div className="section-header">
            <div className="section-title">
              <Zap size={24} />
              <h2>Core Features</h2>
            </div>
            <p className="section-subtitle">Essential functionality for your MVP</p>
          </div>
          <div className="features-grid">
            {generatedConcept.coreFeatures.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-header">
                  <div className="feature-icon">{feature.icon}</div>
                  <h3>{feature.title}</h3>
                </div>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tech Stack */}
        <div className="tech-section">
          <div className="section-header">
            <div className="section-title">
              <Code size={24} />
              <h2>Recommended Tech Stack</h2>
            </div>
            <p className="section-subtitle">Technologies to build your app</p>
          </div>
          <div className="tech-grid">
            {generatedConcept.techStack.map((tech, index) => (
              <div key={index} className="tech-pill">
                <div className="tech-icon">⚡</div>
                <span>{tech}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Action Footer */}
        <div className="action-footer">
          <div className="footer-content">
            <div className="footer-text">
              <h3>Ready to build your app?</h3>
              <p>Save this concept and start turning your shower thought into reality.</p>
            </div>
            <div className="footer-actions">
              <button onClick={handleSave} className="footer-btn secondary">
                <Download size={16} />
                Save to Gallery
              </button>
              <button onClick={() => {
                setGeneratedConcept(null);
                setInputIdea('');
                setShowInputForm(true);
                hasStartedGeneration.current = false;
              }} className="footer-btn primary">
                <Sparkles size={16} />
                Generate Another Idea
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConceptGenerator;